/**
 * Embr "Build a Kit" — framework-agnostic business logic.
 *
 * Ported 1:1 from the interactive prototype (embr_build_kit_prototype.html).
 * No DOM, no Vue, no global state — every function takes its inputs
 * explicitly so it can be called from Vuex (client-side live pricing/gating)
 * AND mirrored server-side (see app/Services/KitPricingService.php)
 * for authoritative price/part-number recomputation on save.
 *
 * Three independent "engines" exist because the real Embr pricing book is
 * organized this way:
 *   1. bespoke3in   — the 3" recessed/canless line (its own hand-tuned data)
 *   2. fd           — the 2"/4"/6" recessed + canless line (data-driven per size)
 *   3. generic      — everything else: Pendant, Monopoint, Track Head,
 *                      Direct Mount, Surface Mount, 1" Recessed (12V AC),
 *                      1" Architectural Recessed, Tape Light & Channel,
 *                      and Downsizer Ring.
 *
 * The catalog itself is served by GET /api/kit-builder/catalog and cached in
 * the `kitBuilder` Vuex module:
 *   import * as rules from '../../kit-builder/kit-builder-rules.js'
 */

/* =========================================================================
   GENERIC ENGINE — data-driven step/option families
   ========================================================================= */

const MR_SERIES_FAMILIES = ['pendant', 'monopoint', 'track_head', 'direct_mount', 'surface_mount'];

export function genCctGateDisabled(code, lumen) {
  if (lumen === '08' || lumen === '10') return code !== 'C5';
  if (code === 'C5') return true;
  if (code === '24') return !['07', '09', '11'].includes(lumen);
  if (code === 'D42') return lumen !== '11';
  if (['35', '40', 'D31'].includes(code)) return lumen === '03';
  return false;
}

export function genBeamGateDisabled(code, lumen, cct, voltage) {
  if (code !== 'T') return false;
  if (!(['03', '07'].includes(lumen) && ['27', '30'].includes(cct))) return true;
  if (voltage === 'UDX') return true; // Universal voltage: 10° beam not available (all MR-series families)
  return false;
}

/**
 * Downsizer Ring helpers -- see the prototype's own comment block for the
 * full spec-sheet reasoning behind each rule.
 */
export function downsizerTargetDisabled(code, sel) {
  if (code === '4') return ['3', '4'].includes(sel.existingHousing);
  return false;
}

export function downsizerStyleDisabled(code, sel) {
  const target = sel.targetSize;
  const standardCodes = ['RD10', 'RD29', 'RD27', 'RD35', 'SQ10', 'SQ29', 'SQ27'];
  const trimless2Codes = ['RD60_2'];
  const trimless34Codes = ['RD60', 'RD55', 'SQ60', 'SQ55'];
  if (target === '2') return !standardCodes.includes(code) && !trimless2Codes.includes(code);
  if (target === '3' || target === '4') return !trimless34Codes.includes(code);
  return true;
}

export function downsizerValidFinishes(sel) {
  const style = sel.style, target = sel.targetSize;
  if (!style) return [];
  if (['RD10', 'RD29', 'RD27'].includes(style)) return ['W', 'BB', 'BZ', 'B', 'H'];
  if (style === 'RD35') return ['W', 'BB'];
  if (['SQ10', 'SQ29', 'SQ27'].includes(style)) return ['W', 'BB', 'B'];
  if (style === 'RD60_2') return ['W', 'BB'];
  if (style === 'RD60' || style === 'RD55') return target === '4' ? ['W', 'BB', 'BZ', 'H'] : ['W', 'BB', 'H'];
  if (style === 'SQ60' || style === 'SQ55') return target === '4' ? ['W', 'BB', 'BZ'] : ['W', 'BB', 'H'];
  return ['W', 'BB'];
}

export function downsizerCctGateDisabled(code, lumen) {
  const sets = {
    '03': ['27', '30'],
    '07': ['24', '27', '30', '35', '40'],
    '09': ['24', '27', '30', '35', '40', 'D31'],
    '11': ['24', '27', '30', '35', '40', 'D31', 'D42'],
    '13': ['24', '27', '30', '35', '40', 'D31', 'D42'],
    '10DX': ['27', '30', '35', '40'],
  };
  return !(sets[lumen] || []).includes(code);
}

export function downsizerRingCode(sel) {
  const big = ['5', '6', '7', '8'].includes(sel.existingHousing);
  const target = sel.targetSize;
  const style = sel.style || '';
  const isSquare = style.startsWith('SQ');
  if (target === '2') {
    if (style === 'RD60_2') return big ? 'EDSR-2SFRD' : 'EL2SFR';
    return big ? 'EDSR-3SFRD' : 'EL3SFR';
  }
  if (target === '3') {
    return isSquare ? (big ? 'EDSR-3SFSQ' : 'EL03SFS') : (big ? 'EDSR-3SFRD' : 'EL3SFR');
  }
  if (target === '4') return isSquare ? 'EDSR-4SFSQ' : 'EDSR-4SFRD';
  return '';
}

export function downsizerRingPrice(sel) {
  const big = ['5', '6', '7', '8'].includes(sel.existingHousing);
  return big ? 45 : 28; // illustrative -- confirm against the real pricing book
}

export function downsizerTrimCode(sel) {
  const target = sel.targetSize;
  const style = sel.style || '';
  const shape = style === 'RD60_2' ? 'RD' : style.slice(0, 2);
  const styleCode = style === 'RD60_2' ? '60' : style.slice(2);
  const finish = sel.finish || '';
  return `MRT${target}${shape}${styleCode}${finish}`;
}

/**
 * The single option-availability gate for every "generic engine" family.
 * Returns true when `code` should be DISABLED given the rest of `sel`.
 */
export function genOptionDisabled(famKey, stepKey, code, sel) {
  if (MR_SERIES_FAMILIES.includes(famKey)) {
    if (stepKey === 'adjustable' || (famKey === 'surface_mount' && stepKey === 'mounting')) {
      if (code === 'AJ') return !['4', '6'].includes(sel.size);
    }
    if (stepKey === 'color') {
      if (code === 'S' && (famKey === 'pendant' || famKey === 'surface_mount')) return sel.size === '6';
      if (code === 'BR' && famKey === 'track_head') return sel.size !== '2';
    }
    if (stepKey === 'lumen') {
      if ((famKey === 'pendant' || famKey === 'surface_mount') && (code === '15' || code === '18')) return !['3', '4'].includes(sel.size);
      if ((famKey === 'monopoint' || famKey === 'track_head') && (code === '15' || code === '18')) return sel.size !== '3';
      if ((famKey === 'pendant' || famKey === 'surface_mount') && code === '20') return sel.size !== '4';
    }
    if (stepKey === 'cct') return genCctGateDisabled(code, sel.lumen);
    if (stepKey === 'beam') return genBeamGateDisabled(code, sel.lumen, sel.cct, sel.voltage);
    return false;
  }
  if (famKey === 'recessed_1in_12v' || famKey === 'recessed_1in_architectural') {
    const adjustableShapes = ['32', '22', '61'];
    const cctAdjustableOnlyShapes = ['32', '22'];
    if (stepKey === 'cct' && ['35', '40', 'D31'].includes(code)) {
      return cctAdjustableOnlyShapes.includes(sel.shape);
    }
    if (stepKey === 'finish') {
      if (adjustableShapes.includes(sel.shape)) {
        const allowed = famKey === 'recessed_1in_12v' ? ['W', 'BB', 'BZ'] : ['W', 'BB'];
        return !allowed.includes(code);
      }
      return false;
    }
    if (famKey === 'recessed_1in_architectural' && stepKey === 'housingSpec' && code === 'R60ICA') {
      return sel.cutout === 'RMD';
    }
    return false;
  }
  if (famKey === 'downsizer_ring') {
    if (stepKey === 'targetSize') return downsizerTargetDisabled(code, sel);
    if (stepKey === 'style') return downsizerStyleDisabled(code, sel);
    if (stepKey === 'finish') return !downsizerValidFinishes(sel).includes(code);
    if (stepKey === 'cct') return downsizerCctGateDisabled(code, sel.lumen);
    if (stepKey === 'beam') return genBeamGateDisabled(code, sel.lumen, sel.cct, null);
    return false;
  }
  return false;
}

/** Group a family's flat step list into physically-separate part-number segments. */
export function genSegments(fam) {
  const segs = [];
  fam.steps.forEach((step, idx) => {
    if (idx === 0 || step.partPrefix != null) segs.push({ steps: [] });
    segs[segs.length - 1].steps.push(step);
  });
  return segs;
}

export function genSegmentComplete(seg, sel) {
  return seg.steps.every((s) => !!sel[s.key]);
}

export function genComputeUnitPrice(fam, sel, accessories, secondaryOptics) {
  let total = 0;
  fam.steps.forEach((step) => {
    const code = sel[step.key];
    if (code == null) return;
    const opt = (step.options || []).find((o) => o.code === code);
    if (opt) total += opt.price || 0;
  });
  total += (accessories || []).reduce((a, c) => {
    const o = (secondaryOptics || []).find((x) => x.code === c);
    return a + (o ? o.price : 0);
  }, 0);
  if (sel.shape === '61' && fam.spackleAccessory) total += fam.spackleAccessory.price || 0;
  if (fam.key === 'downsizer_ring' && sel.existingHousing && sel.targetSize && sel.style) {
    total += downsizerRingPrice(sel);
  }
  return total;
}

// Real Embr SKUs for these 5 families always carry a 2-letter family code
// right after the size digit (MR2PDW-0327-12, not MR2-W03-27-12) -- confirmed
// against Embr's actual price book. Kept as a special case rather than a
// generic data-driven rule because the real grammar doesn't follow the
// simple "dash before a digit-suffix" pattern the rest of genBuildPartNumber
// uses: block1 (size+familyCode+[AJ]+color) has no internal dashes, then a
// single dash, then block2 (lumen+cct+[voltage]) also has no internal
// dashes, then optional dash+beam and dash+mounting suffixes.
export const CYLINDER_FAMILY_CODES = {
  pendant: 'PD',
  monopoint: 'MP',
  track_head: 'TR',
  direct_mount: 'DM',
  surface_mount: 'SF',
};

export function buildCylinderPartNumber(fam, sel) {
  const famCode = CYLINDER_FAMILY_CODES[fam.key];
  let block1 = (fam.partPrefix || 'MR') + (sel.size || '') + famCode;
  // "Adjustable" lives under different step keys per family: Pendant has a
  // dedicated 'adjustable' step; Surface Mount reuses its 'mounting' step for
  // the same Fixed/Adjustable choice (it has no stem-length option at all).
  if (fam.key === 'pendant' && sel.adjustable === 'AJ') block1 += 'AJ';
  if (fam.key === 'surface_mount' && sel.mounting === 'AJ') block1 += 'AJ';
  block1 += sel.color || '';
  let block2 = (sel.lumen || '') + (sel.cct || '');
  if (sel.voltage === 'UDX') block2 += 'UDX';
  let pn = block1 + '-' + block2;
  if (sel.beam && sel.beam !== 'DEFAULT') pn += '-' + sel.beam;
  // Stem-length mounting suffix only applies to Pendant/Monopoint/Track Head --
  // Surface Mount's 'mounting' field means Fixed/AJ (handled above) and
  // Direct Mount has no mounting step at all.
  if (['pendant', 'monopoint', 'track_head'].includes(fam.key) && sel.mounting && sel.mounting !== '(blank)') {
    pn += '-' + sel.mounting;
  }
  return pn;
}

export function genBuildPartNumber(fam, sel) {
  if (CYLINDER_FAMILY_CODES[fam.key]) {
    return buildCylinderPartNumber(fam, sel);
  }
  const segments = [];
  let current = fam.partPrefix || '';
  fam.steps.forEach((step) => {
    if (step.partPrefix != null) {
      if (current) segments.push(current);
      current = step.partPrefix;
    }
    if (step.skipPartNumber) return;
    const code = sel[step.key];
    if (code == null || code === '(blank)' || code === 'DEFAULT') return;
    current += (current.match(/[0-9]$/) ? '-' : '') + code;
  });
  if (current) segments.push(current);
  if (sel.shape === '61' && fam.spackleAccessory) segments.push(fam.spackleAccessory.code);
  if (fam.key === 'downsizer_ring' && sel.existingHousing && sel.targetSize && sel.style && sel.finish) {
    segments.unshift(downsizerRingCode(sel) + ' + ' + downsizerTrimCode(sel));
  }
  return segments.join(' + ');
}

/* =========================================================================
   FD ENGINE — 2"/4"/6" recessed + canless (data-driven per size)
   ========================================================================= */

export function fdStepComplete(sel, stepKey, fixtureType) {
  if (stepKey === 'housing') return !!sel.application && !!sel.voltage;
  if (stepKey === 'frame') return !!sel.frameSpec && !!sel.voltage && !!sel.frameOptional;
  if (stepKey === 'light') return !!sel.cct && !!sel.lumen && !!sel.beam;
  if (stepKey === 'trim') {
    if (!sel.trimFamily) return false;
    if (sel.trimFamily === 'trimless') return !!sel.trimlessShape && !!sel.trimStyle && !!sel.trimFinish && !!sel.mountAdd;
    return !!sel.trimStyle && !!sel.trimFinish;
  }
  return true;
}

export function fdLumensForCct(fd, cctCode, voltage) {
  const pool = voltage === 'UDX' ? fd.lumensUDX || [] : fd.lumensStd || [];
  return pool.filter((l) => (fd.cctSets[l.cctSet] || []).includes(cctCode));
}

export function fdBuildPartNumbers(fd, sel, fixtureType) {
  const parts = {};
  if (fixtureType === 'can') {
    parts.housing = sel.application ? `${fd.housingPartPrefix}${sel.application}${sel.voltage === 'UDX' ? '-UDX' : ''}-ICA` : null;
  } else {
    parts.frame = sel.frameSpec ? `${fd.housingPartPrefix}${sel.frameSpec}-10-${sel.voltage || 'IC'}${sel.frameOptional === 'EM' ? '-EM' : ''}` : null;
  }
  if (sel.lumen) {
    const beamSuffix = sel.beam && sel.beam !== 'DEFAULT' ? '-' + sel.beam : '';
    parts.module = `${fd.lumenModulePartPrefix}${sel.lumen}${sel.cct || ''}${beamSuffix}`;
  }
  if (sel.trimStyle) {
    const prefix = sel.trimFamily === 'trimless' ? fd.trimlessPartPrefix || fd.trimPartPrefix : fd.trimPartPrefix;
    const shapeCode = sel.trimFamily === 'trimless' ? sel.trimlessShape : sel.trimFamily === 'round' ? 'RD' : 'SQ';
    parts.trim = `${prefix}${shapeCode}${sel.trimStyle}${sel.trimFinish || ''}`;
  }
  if (sel.trimFamily === 'trimless' && sel.mountAdd) {
    const spackle = (fd.spackleAccessory || {})[sel.trimlessShape];
    parts.spackle = spackle ? spackle.code : null;
  }
  if ((sel.accessories || []).length) parts.accessories = sel.accessories.join(', ');
  return parts;
}

export function fdComputeUnitPrice(fd, sel, fixtureType, secondaryOptics) {
  let total = 0;
  if (fixtureType === 'can') {
    total += fd.housingSizePrice || 0;
    const a = (fd.applications || []).find((x) => x.code === sel.application);
    if (a) total += a.price;
    const v = (fd.voltages || []).find((x) => x.code === sel.voltage);
    if (v) total += v.price;
  } else {
    const f = (fd.frameSpec || []).find((x) => x.code === sel.frameSpec);
    if (f) total += f.price;
    const v = (fd.voltages || []).find((x) => x.code === sel.voltage);
    if (v) total += v.price;
    const o = (fd.canlessOptional || []).find((x) => x.code === sel.frameOptional);
    if (o) total += o.price;
  }
  if (sel.lumen) {
    const pool = sel.voltage === 'UDX' ? fd.lumensUDX || [] : fd.lumensStd || [];
    const l = pool.find((x) => x.code === sel.lumen);
    if (l) total += l.price;
  }
  if (sel.cct) {
    const c = (fd.cctList || {})[sel.cct];
    if (c) total += c.price || 0;
  }
  if (sel.beam) {
    const b = (fd.beams || []).find((x) => x.code === sel.beam);
    if (b) total += b.price;
  }
  if (sel.trimFamily === 'trimless') {
    total += (fd.trimSizePrice || {}).trimless || 0;
    total += (fd.trimlessStylePrice || {})[sel.trimStyle] || 0;
    total += (fd.trimlessShapePrice || {})[sel.trimlessShape] || 0;
    if (sel.mountAdd) {
      const sp = (fd.spackleAccessory || {})[sel.trimlessShape];
      if (sp) total += sp.price || 0;
    }
  } else if (sel.trimFamily) {
    total += (fd.trimSizePrice || {})[sel.trimFamily] || 0;
    total += ((fd.trimStylePrice || {})[sel.trimFamily] || {})[sel.trimStyle] || 0;
  }
  if (sel.trimFinish) {
    const fin = (fd.finishes || {})[sel.trimFinish];
    if (fin) total += fin.price || 0;
  }
  (sel.accessories || []).forEach((code) => {
    const o = (secondaryOptics || []).find((x) => x.code === code);
    if (o) total += o.price;
  });
  return total;
}

/* =========================================================================
   3" BESPOKE ENGINE — the original hand-tuned 3" recessed/canless line
   ========================================================================= */

export function stepComplete3in(sel, stepKey) {
  if (stepKey === 'housing') return !!sel.application && !!sel.voltage;
  if (stepKey === 'frame') return !!sel.frameSpec && !!sel.voltage && !!sel.frameOptional;
  if (stepKey === 'light') return !!sel.cct && !!sel.lumen && !!sel.beam;
  if (stepKey === 'trim') {
    if (!sel.trimFamily) return false;
    if (sel.trimFamily === 'trimless') return !!sel.trimlessShape && !!sel.trimStyle && !!sel.trimFinish && !!sel.mountAdd;
    return !!sel.trimStyle && !!sel.trimFinish;
  }
  return true;
}

export function buildPartNumbers3in(sel, fixtureType, mountAccessory) {
  const parts = {};
  if (fixtureType === 'can') {
    parts.housing = sel.application ? `MRH3${sel.application}${sel.voltage === 'UDX' ? '-UDX' : ''}-ICA` : null;
  } else {
    parts.frame = sel.frameSpec ? `MRP${sel.frameSpec}-10-${sel.voltage || 'IC'}${sel.frameOptional === 'EM' ? '-EM' : ''}` : null;
  }
  if (sel.lumen) {
    const beamSuffix = sel.beam && sel.beam !== 'DEFAULT' ? '-' + sel.beam : '';
    parts.module = fixtureType === 'canless' ? `MRCL21${sel.cct || ''}${beamSuffix}` : `MRL${sel.lumen}${sel.cct || ''}${beamSuffix}`;
  }
  if (sel.trimStyle) {
    const prefix = fixtureType === 'canless' && sel.trimFamily !== 'trimless' ? 'MRCLT' : 'MRT';
    const shapeCode = sel.trimFamily === 'trimless' ? sel.trimlessShape : sel.trimFamily === 'round' ? 'RD' : 'SQ';
    parts.trim = `${prefix}3${shapeCode}${sel.trimStyle}${sel.trimFinish || ''}`;
  }
  if (sel.trimFamily === 'trimless' && sel.mountAdd && mountAccessory) {
    const spackle = mountAccessory[sel.trimlessShape];
    parts.spackle = spackle ? spackle.code : null;
  }
  if ((sel.accessories || []).length || (sel.mountAdd && sel.trimFamily !== 'trimless')) {
    const list = [...(sel.accessories || [])];
    if (sel.mountAdd && sel.trimFamily !== 'trimless' && mountAccessory) {
      const m = mountAccessory[sel.trimFamily];
      if (m) list.push(m.code);
    }
    parts.accessories = list.join(', ');
  }
  return parts;
}

export function computeUnitPrice3in(sel, fixtureType, consts, secondaryOptics) {
  const fam = fixtureType === 'canless' ? 'canless' : 'can';
  let total = 0;
  if (fixtureType === 'can') {
    total += consts.HOUSING_SIZE_PRICE;
    const a = consts.APPLICATIONS.find((x) => x.code === sel.application);
    if (a) total += a.price;
    const v = consts.VOLTAGES_CAN.find((x) => x.code === sel.voltage);
    if (v) total += v.price;
  } else {
    const f = consts.FRAME_SPEC.find((x) => x.code === sel.frameSpec);
    if (f) total += f.price;
    const v = consts.VOLTAGES_CANLESS.find((x) => x.code === sel.voltage);
    if (v) total += v.price;
    const o = consts.CANLESS_OPTIONAL.find((x) => x.code === sel.frameOptional);
    if (o) total += o.price;
  }
  if (sel.lumen) {
    if (fixtureType === 'canless') total += consts.LUMEN_CANLESS.price;
    else {
      const l = (sel.voltage === 'UDX' ? consts.LUMENS_CAN_UDX : consts.LUMENS_CAN_STD).find((x) => x.code === sel.lumen);
      if (l) total += l.price;
    }
  }
  if (sel.cct) {
    const cctData = fixtureType === 'canless' ? consts.CCT_LIST_CANLESS[sel.cct] : consts.CCT_LIST[sel.cct];
    if (cctData) total += cctData.price;
  }
  if (sel.beam) {
    const b = consts.beamListResult.find((x) => x.code === sel.beam);
    if (b) total += b.price;
  }
  if (sel.trimFamily) {
    total += consts.TRIM_SIZE_PRICE[fam][sel.trimFamily] || 0;
    if (sel.trimFamily === 'trimless') {
      if (sel.trimlessShape) total += consts.TRIMLESS_SHAPE_PRICE[sel.trimlessShape] || 0;
      if (sel.trimStyle) total += consts.TRIMLESS_STYLE_PRICE[sel.trimStyle] || 0;
      if (sel.mountAdd) {
        const spackle = consts.MOUNT_ACCESSORY[sel.trimlessShape];
        if (spackle) total += spackle.price;
      }
    } else if (sel.trimStyle) {
      total += (consts.TRIM_STYLE_PRICE[fam][sel.trimFamily] || {})[sel.trimStyle] || 0;
    }
  }
  if (sel.trimFinish) {
    const f = consts.FINISHES[sel.trimFinish];
    if (f) total += f.price;
  }
  (sel.accessories || []).forEach((code) => {
    const o = secondaryOptics.find((x) => x.code === code);
    if (o) total += o.price;
  });
  if (sel.mountAdd && sel.trimFamily !== 'trimless') {
    const mount = consts.MOUNT_ACCESSORY[sel.trimFamily];
    if (mount) total += mount.price;
  }
  return total;
}

/* =========================================================================
   COMPONENT BREAKDOWN

   A configured kit is not one product -- it is several separately-stocked
   items (a housing, a light module, a trim, sometimes a spackle frame or a
   secondary optic), each with its own part number and its own price. The
   functions above return the assembled kit's single total; the ones below
   split that same total into the individual items, so a quote or an order can
   list them line by line.

   INVARIANT, enforced by tests/KitBuilder/verify_js_vs_php.mjs:
     sum(component prices)  === the engine's computeUnitPrice(), and
     the component part numbers reassemble to the engine's buildPartNumber(s).
   Change a price rule above and the matching breakdown below must move with
   it -- and so must the PHP mirror in app/Services/KitPricingService.php.
   ========================================================================= */

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Labels for the generic engine's part-number segments, per family. */
const GEN_SEGMENT_LABELS = {
  recessed_1in_architectural: ['Housing', 'Fixture'],
  tape_light: ['Channel', 'Tape Light'],
  downsizer_ring: ['Trim', 'Light Module'],
};

/**
 * Walks a generic family's steps exactly the way genBuildPartNumber does, but
 * accumulating each segment's price alongside its part number.
 */
export function genPartSegments(fam, sel) {
  const segments = [];
  let current = { partNumber: fam.partPrefix || '', price: 0 };

  fam.steps.forEach((step) => {
    // A step prefix always starts a new segment -- including on the very first
    // step, where it REPLACES the family prefix (that is how the 1"
    // architectural housing gets its MRHA prefix). genBuildPartNumber drops the
    // resulting empty leading segment; this keeps it so no price is lost, and
    // genComputeComponents filters it out instead.
    if (step.partPrefix != null) {
      segments.push(current);
      current = { partNumber: step.partPrefix, price: 0 };
    }

    const code = sel[step.key];
    if (code != null) {
      const opt = (step.options || []).find((o) => o.code === code);
      if (opt) current.price += opt.price || 0;
    }

    if (step.skipPartNumber) return;
    if (code == null || code === '(blank)' || code === 'DEFAULT') return;
    current.partNumber += (current.partNumber.match(/[0-9]$/) ? '-' : '') + code;
  });

  segments.push(current);
  return segments;
}

export function genComputeComponents(fam, sel, accessories, secondaryOptics) {
  // A cylinder fixture is one assembled product with one SKU, built by the
  // special grammar above rather than from part-number segments.
  if (CYLINDER_FAMILY_CODES[fam.key]) {
    const out = [
      {
        key: 'segment_0',
        label: fam.label,
        part_number: buildCylinderPartNumber(fam, sel),
        price: round2(
          fam.steps.reduce((total, step) => {
            const code = sel[step.key];
            if (code == null) return total;
            const opt = (step.options || []).find((o) => o.code === code);
            return total + (opt ? opt.price || 0 : 0);
          }, 0)
        ),
      },
    ];
    (accessories || []).forEach((code) => {
      const o = (secondaryOptics || []).find((x) => x.code === code);
      if (o) {
        out.push({ key: 'accessory_' + code, label: o.name, part_number: o.code, price: round2(o.price || 0) });
      }
    });
    return out;
  }

  const segments = genPartSegments(fam, sel);
  const labels = GEN_SEGMENT_LABELS[fam.key] || [];
  const isDownsizer = fam.key === 'downsizer_ring';
  const hasRing = isDownsizer && !!sel.existingHousing && !!sel.targetSize && !!sel.style && !!sel.finish;
  const out = [];

  // The downsizer's ring is its own SKU, added on top of the picked options.
  if (hasRing) {
    out.push({
      key: 'ring',
      label: 'Downsizer Ring',
      part_number: downsizerRingCode(sel),
      price: round2(downsizerRingPrice(sel)),
    });
  }

  // The downsizer's first segment prices the trim but contributes no part
  // number of its own -- its SKU comes from downsizerTrimCode().
  if (isDownsizer && hasRing && segments.length) segments[0].partNumber = downsizerTrimCode(sel);

  const emitted = segments.filter((seg) => !!seg.partNumber);
  // Any priced segment with no part number (a partially-configured downsizer)
  // would otherwise drop money out of the breakdown -- fold it into the first
  // real line so the components always still sum to the kit price.
  const orphanPrice = segments.filter((seg) => !seg.partNumber).reduce((a, seg) => a + seg.price, 0);

  emitted.forEach((seg, idx) => {
    out.push({
      key: 'segment_' + idx,
      label: labels[idx] || (emitted.length === 1 ? fam.label : 'Part ' + (idx + 1)),
      part_number: seg.partNumber,
      price: round2(seg.price + (idx === 0 ? orphanPrice : 0)),
    });
  });

  if (sel.shape === '61' && fam.spackleAccessory) {
    out.push({
      key: 'spackle',
      label: fam.spackleAccessory.name || 'Spackle Frame',
      part_number: fam.spackleAccessory.code,
      price: round2(fam.spackleAccessory.price || 0),
    });
  }

  (accessories || []).forEach((code) => {
    const o = (secondaryOptics || []).find((x) => x.code === code);
    if (o) {
      out.push({ key: 'accessory_' + code, label: o.name, part_number: o.code, price: round2(o.price || 0) });
    }
  });

  return out;
}

export function fdComputeComponents(fd, sel, fixtureType, secondaryOptics) {
  const parts = fdBuildPartNumbers(fd, sel, fixtureType);
  const out = [];

  let shellPrice = 0;
  if (fixtureType === 'can') {
    shellPrice += fd.housingSizePrice || 0;
    const a = (fd.applications || []).find((x) => x.code === sel.application);
    if (a) shellPrice += a.price;
    const v = (fd.voltages || []).find((x) => x.code === sel.voltage);
    if (v) shellPrice += v.price;
  } else {
    const f = (fd.frameSpec || []).find((x) => x.code === sel.frameSpec);
    if (f) shellPrice += f.price;
    const v = (fd.voltages || []).find((x) => x.code === sel.voltage);
    if (v) shellPrice += v.price;
    const o = (fd.canlessOptional || []).find((x) => x.code === sel.frameOptional);
    if (o) shellPrice += o.price;
  }
  const shellPart = fixtureType === 'canless' ? parts.frame : parts.housing;
  if (shellPart) {
    out.push({
      key: fixtureType === 'canless' ? 'frame' : 'housing',
      label: fixtureType === 'canless' ? 'Frame & Power Pack' : 'Housing',
      part_number: shellPart,
      price: round2(shellPrice),
    });
  }

  let modulePrice = 0;
  if (sel.lumen) {
    const pool = sel.voltage === 'UDX' ? fd.lumensUDX || [] : fd.lumensStd || [];
    const l = pool.find((x) => x.code === sel.lumen);
    if (l) modulePrice += l.price;
  }
  if (sel.cct) {
    const c = (fd.cctList || {})[sel.cct];
    if (c) modulePrice += c.price || 0;
  }
  if (sel.beam) {
    const b = (fd.beams || []).find((x) => x.code === sel.beam);
    if (b) modulePrice += b.price;
  }
  if (parts.module) {
    out.push({ key: 'module', label: 'LED Module', part_number: parts.module, price: round2(modulePrice) });
  }

  let trimPrice = 0;
  if (sel.trimFamily === 'trimless') {
    trimPrice += (fd.trimSizePrice || {}).trimless || 0;
    trimPrice += (fd.trimlessStylePrice || {})[sel.trimStyle] || 0;
    trimPrice += (fd.trimlessShapePrice || {})[sel.trimlessShape] || 0;
  } else if (sel.trimFamily) {
    trimPrice += (fd.trimSizePrice || {})[sel.trimFamily] || 0;
    trimPrice += ((fd.trimStylePrice || {})[sel.trimFamily] || {})[sel.trimStyle] || 0;
  }
  if (sel.trimFinish) {
    const fin = (fd.finishes || {})[sel.trimFinish];
    if (fin) trimPrice += fin.price || 0;
  }
  if (parts.trim) {
    out.push({ key: 'trim', label: 'Trim', part_number: parts.trim, price: round2(trimPrice) });
  }

  if (sel.trimFamily === 'trimless' && sel.mountAdd) {
    const sp = (fd.spackleAccessory || {})[sel.trimlessShape];
    if (sp) {
      out.push({ key: 'spackle', label: sp.name || 'Spackle Frame', part_number: sp.code, price: round2(sp.price || 0) });
    }
  }

  (sel.accessories || []).forEach((code) => {
    const o = (secondaryOptics || []).find((x) => x.code === code);
    if (o) {
      out.push({ key: 'accessory_' + code, label: o.name, part_number: o.code, price: round2(o.price || 0) });
    }
  });

  return out;
}

export function computeComponents3in(sel, fixtureType, consts, secondaryOptics) {
  const fam = fixtureType === 'canless' ? 'canless' : 'can';
  const parts = buildPartNumbers3in(sel, fixtureType, consts.MOUNT_ACCESSORY);
  const out = [];

  let shellPrice = 0;
  if (fixtureType === 'can') {
    shellPrice += consts.HOUSING_SIZE_PRICE;
    const a = consts.APPLICATIONS.find((x) => x.code === sel.application);
    if (a) shellPrice += a.price;
    const v = consts.VOLTAGES_CAN.find((x) => x.code === sel.voltage);
    if (v) shellPrice += v.price;
  } else {
    const f = consts.FRAME_SPEC.find((x) => x.code === sel.frameSpec);
    if (f) shellPrice += f.price;
    const v = consts.VOLTAGES_CANLESS.find((x) => x.code === sel.voltage);
    if (v) shellPrice += v.price;
    const o = consts.CANLESS_OPTIONAL.find((x) => x.code === sel.frameOptional);
    if (o) shellPrice += o.price;
  }
  const shellPart = fixtureType === 'canless' ? parts.frame : parts.housing;
  if (shellPart) {
    out.push({
      key: fixtureType === 'canless' ? 'frame' : 'housing',
      label: fixtureType === 'canless' ? 'Frame & Power Pack' : 'Housing',
      part_number: shellPart,
      price: round2(shellPrice),
    });
  }

  let modulePrice = 0;
  if (sel.lumen) {
    if (fixtureType === 'canless') modulePrice += consts.LUMEN_CANLESS.price;
    else {
      const pool = sel.voltage === 'UDX' ? consts.LUMENS_CAN_UDX : consts.LUMENS_CAN_STD;
      const l = pool.find((x) => x.code === sel.lumen);
      if (l) modulePrice += l.price;
    }
  }
  if (sel.cct) {
    const cctData = fixtureType === 'canless' ? consts.CCT_LIST_CANLESS[sel.cct] : consts.CCT_LIST[sel.cct];
    if (cctData) modulePrice += cctData.price;
  }
  if (sel.beam) {
    const b = consts.beamListResult.find((x) => x.code === sel.beam);
    if (b) modulePrice += b.price;
  }
  if (parts.module) {
    out.push({ key: 'module', label: 'LED Module', part_number: parts.module, price: round2(modulePrice) });
  }

  let trimPrice = 0;
  if (sel.trimFamily) {
    trimPrice += consts.TRIM_SIZE_PRICE[fam][sel.trimFamily] || 0;
    if (sel.trimFamily === 'trimless') {
      if (sel.trimlessShape) trimPrice += consts.TRIMLESS_SHAPE_PRICE[sel.trimlessShape] || 0;
      if (sel.trimStyle) trimPrice += consts.TRIMLESS_STYLE_PRICE[sel.trimStyle] || 0;
    } else if (sel.trimStyle) {
      trimPrice += (consts.TRIM_STYLE_PRICE[fam][sel.trimFamily] || {})[sel.trimStyle] || 0;
    }
  }
  if (sel.trimFinish) {
    const f = consts.FINISHES[sel.trimFinish];
    if (f) trimPrice += f.price;
  }
  if (parts.trim) {
    out.push({ key: 'trim', label: 'Trim', part_number: parts.trim, price: round2(trimPrice) });
  }

  if (sel.trimFamily === 'trimless' && sel.mountAdd) {
    const spackle = consts.MOUNT_ACCESSORY[sel.trimlessShape];
    if (spackle) {
      out.push({ key: 'spackle', label: spackle.name || 'Spackle Frame', part_number: spackle.code, price: round2(spackle.price) });
    }
  }

  (sel.accessories || []).forEach((code) => {
    const o = secondaryOptics.find((x) => x.code === code);
    if (o) {
      out.push({ key: 'accessory_' + code, label: o.name, part_number: o.code, price: round2(o.price || 0) });
    }
  });

  if (sel.mountAdd && sel.trimFamily !== 'trimless') {
    const mount = consts.MOUNT_ACCESSORY[sel.trimFamily];
    if (mount) {
      out.push({ key: 'mount', label: mount.name || 'Mount Adapter', part_number: mount.code, price: round2(mount.price) });
    }
  }

  return out;
}
